import { motion } from "framer-motion";
import { ChevronRight, Truck, Sparkles, ShieldCheck, Clock, Flower2 } from "lucide-react";
import { marqueeItems } from "../data/siteData";
import { optimizeImg } from "../lib/img";
import { useZeoContent } from "../lib/ZeoContentContext";

const DEFAULT_HERO_IMG = "https://images.unsplash.com/photo-1572454591674-2739f30d8c40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBmbG9yYWwlMjBhcnJhbmdlbWVudCUyMHByZW1pdW0lMjBib3VxdWV0fGVufDB8fHx8MTc4Mjc5NjcxMHww&ixlib=rb-4.1.0&q=85";

const badges = [
  { icon: Truck, label: "Aynı Gün Teslimat" },
  { icon: Sparkles, label: "Taze Çiçek Garantisi" },
  { icon: ShieldCheck, label: "Güvenli Sipariş" },
  { icon: Clock, label: "7/24 Hizmet" },
];

export default function Hero() {
  const { heroImage } = useZeoContent();
  const displayImg = heroImage || DEFAULT_HERO_IMG;

  return (
    <section
      id="hero"
      className="relative w-full overflow-hidden bg-[#F8F8F8]"
      data-testid="hero-section"
    >
      <div className="frida-container relative pt-10 lg:pt-16 pb-16 lg:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: Text */}
          <div className="lg:col-span-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#EAEAEA] mb-7"
              data-testid="hero-badge"
            >
              <span className="w-2 h-2 rounded-full bg-[#A85C34] animate-pulse" style={{ backgroundColor: "var(--frida-primary)" }} />
              <span className="text-xs tracking-[0.2em] uppercase text-[#222222] font-medium">
                Türkiye&apos;nin Premium Çiçekçisi
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
              className="font-serif text-[40px] sm:text-5xl lg:text-[64px] xl:text-[72px] leading-[1.05] tracking-tight text-[#222222] text-balance"
              data-testid="hero-title"
            >
              Duygularınızı{" "}
              <span className="italic text-[#A85C34]" style={{ color: "var(--frida-primary)" }}>
                En Zarif
              </span>{" "}
              Çiçeklerle Buluşturuyoruz
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25, ease: "easeOut" }}
              className="mt-7 text-lg lg:text-xl text-[#555555] leading-relaxed max-w-xl text-pretty"
              data-testid="hero-subtitle"
            >
              Her çiçek bir duyguyu anlatır. Siz duygunuzu seçin, biz Hatay, Adana, Osmaniye ve
              Gaziantep&apos;e aynı gün, Türkiye&apos;nin her iline kargo ile en zarif haliyle ulaştıralım.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4, ease: "easeOut" }}
              className="mt-9 flex flex-wrap items-center gap-3"
            >
              <a
                href="#urunler"
                className="group inline-flex items-center gap-2 px-7 py-4 bg-[#A85C34] hover:bg-[#8B4726] text-white rounded-full font-medium text-base transition-all duration-300 active:scale-95 shadow-[0_8px_30px_rgba(168,92,52,0.25)]"
                style={{ backgroundColor: "var(--frida-primary)" }}
                data-testid="hero-cta-primary"
              >
                Sipariş Ver
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#kategoriler"
                className="inline-flex items-center gap-2 px-7 py-4 border border-[#222222] text-[#222222] hover:bg-[#222222] hover:text-white rounded-full font-medium text-base transition-all duration-300"
                data-testid="hero-cta-secondary"
              >
                Kategorileri İncele
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl"
              data-testid="hero-badges"
            >
              {badges.map((b) => (
                <div
                  key={b.label}
                  className="flex items-center gap-2 text-sm text-[#555555]"
                >
                  <span className="w-8 h-8 rounded-full bg-white border border-[#EAEAEA] flex items-center justify-center shrink-0">
                    <b.icon className="w-4 h-4 text-[#A85C34]" style={{ color: "var(--frida-primary)" }} />
                  </span>
                  <span className="font-medium">{b.label}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: Image */}
          <div className="lg:col-span-6 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="relative aspect-[4/5] lg:aspect-[5/6] rounded-[32px] overflow-hidden bg-[#EAEAEA]"
            >
              <img
                src={optimizeImg(displayImg, 900)}
                alt="Premium çiçek aranjmanı"
                width="900"
                height="1080"
                className="w-full h-full object-cover"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />

              {/* Marka mührü */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.5 }}
                className="absolute top-6 left-6 w-28 h-28 sm:w-32 sm:h-32 rounded-full bg-white shadow-[0_10px_40px_rgba(0,0,0,0.2)] p-2"
                data-testid="hero-logo-badge"
              >
                <img
                  src="/frida-cicekcilik-logo.png"
                  alt="Frida Çiçekçilik"
                  width="128"
                  height="128"
                  className="w-full h-full object-contain"
                />
              </motion.div>

              {/* Floating rating card */}
              <motion.div
                initial={{ opacity: 0, x: -20, y: 20 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="absolute bottom-6 left-6 right-6 sm:right-auto sm:max-w-[280px] bg-white/95 backdrop-blur-md rounded-2xl p-5 shadow-[0_20px_60px_rgba(0,0,0,0.15)]"
                data-testid="hero-floating-card"
              >
                <div className="flex items-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 fill-[#D4A15C]" style={{ fill: "var(--frida-accent)" }} viewBox="0 0 20 20">
                      <path d="M10 1.5l2.6 5.5 6 .6-4.5 4.3 1.2 6L10 15l-5.3 2.9 1.2-6L1.4 7.6l6-.6L10 1.5z" />
                    </svg>
                  ))}
                  <span className="ml-2 font-bold text-[#222222]">5.0</span>
                </div>
                <p className="text-sm text-[#555555] leading-relaxed">
                  <span className="font-semibold text-[#222222]">150+ Google Yorumu</span>
                  <br />
                  ile bölgenin en sevilen çiçekçisi.
                </p>
              </motion.div>
            </motion.div>

            {/* Decorative gold accent */}
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[#D4A15C]/20 blur-2xl pointer-events-none" style={{ backgroundColor: "color-mix(in srgb, var(--frida-accent) 20%, transparent)" }} />
            <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-[#A85C34]/10 blur-3xl pointer-events-none" style={{ backgroundColor: "color-mix(in srgb, var(--frida-primary) 10%, transparent)" }} />
          </div>
        </div>
      </div>

      {/* Marquee bottom */}
      <div className="relative border-y border-[#EAEAEA] bg-[#FBF9F4] overflow-hidden">
        <div className="flex marquee-track gap-10 py-6 whitespace-nowrap">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex gap-10" aria-hidden={i === 1}>
              {marqueeItems.map((t, j) => (
                <span key={`${i}-${j}`} className="flex items-center gap-10">
                  <span className="flex items-center gap-2.5 text-[#222222] font-serif italic text-lg lg:text-xl">
                    <Flower2 className="w-4 h-4 text-[#A85C34] not-italic shrink-0" style={{ color: "var(--frida-primary)" }} />
                    {t}
                  </span>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#D4A15C] shrink-0" style={{ backgroundColor: "var(--frida-accent)" }} />
                </span>
              ))}
            </div>
          ))}
        </div>
        {/* Edge fade for a more polished look */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 lg:w-32 bg-gradient-to-r from-[#FBF9F4] to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 lg:w-32 bg-gradient-to-l from-[#FBF9F4] to-transparent" />
      </div>
    </section>
  );
}
